import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import DoctorsFilterOptions from "../../../components/Common/CommonFilterOptions";
import Deletepopup from "../../../components/Modal/Deletepopup";

// Updated custom hook paths
import Actions from "../../../components/Common/Actions";
import Breadcrumbs from "../../../components/Common/Breadcrumbs/AntdBreadcrumbs";
import FilledButton from "../../../components/Common/Buttons/FilledButton";
import ListPageLayout from "../../../components/Layout";
import useDebouncedSearch from "../../../components/Utils/hooks/useDebouncedSearch";
import useDeleteHandler from "../../../components/Utils/hooks/useDeleteHandler";
import usePageChange from "../../../components/Utils/hooks/usePageChange";
import usePaginatedData from "../../../components/Utils/hooks/usePaginatedData";
import useSortHandler from "../../../components/Utils/hooks/useSortHandler";

const ClinicService = () => {
  const navigate = useNavigate();
  const addClinicService = {
    id: "addnewpost",
    onClick: () => navigate("/doctor-clinic-service-create"),
    style: { width: "240px" },
    label: "Add Clinic Services",
  };
  // Sorting
  const [sort, setSort] = useState("");
  const [sortName, setSortName] = useState("Newest First");
  const handleSortClick = useSortHandler(setSort, setSortName);

  // Search
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedSearch(setSearch, () =>
    setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 })),
  );

  // Filter states
  const [selectedValues, setSelectedValues] = useState({
    "Primary Specialisation": [],
    Status: [],
  });
  const [filteringKey, setFilteringKey] = useState([]);
  const [tempSelectedValues, setTempSelectedValues] = useState({
    "Primary Specialisation": [],
    Status: [],
  });

  const [selectedCategory, setSelectedCategory] = useState(
    "Primary Specialisation",
  );
  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);
  const [loader, setLoader] = useState(true);

  // Delete state
  const [deleteId, setDeleteId] = useState("");
  const [showDelete, setShowDelete] = useState(false);

  // Pagination
  const { records, totalCount, pagination, setPagination, fetchData } =
    usePaginatedData({
      apiUrl: `${process.env.REACT_APP_API_URL}doctors-clinic/list`,
      initialParams: {
        sort,
        searchText: search,
        selectedValues,
      },
    });

  const handlePageChange = usePageChange(pagination, setPagination);
  const handleDelete = useDeleteHandler(
    "doctors-clinic",
    fetchData,
    setShowDelete,
  );

  useEffect(() => {
    setLoader(true)
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.skip, pagination.limit, sort, search, selectedValues]);

  const handleActive = (id, status) => {
    setLoader(true)
    axios
      .patch(
        `${process.env.REACT_APP_API_URL}doctors-clinic/update/active/${id}`,
        {
          active: !status,
        },
      )
      .then((result) => {
        fetchData();
      });
  };

  const columns = [
    {
      title: "S.No",
      render: (text, record, index) =>
        Number(pagination.skip || 0) + (index + 1),
      key: "serial",
      width: 80,
      fixed: "left",
    },
    {
      title: "Specializations",
      dataIndex: ["specialization", "inclinic_doctor_specialization"],
      key: "specialization",
      fixed: "left",
    },
    {
      title: "Clinic Services",
      dataIndex: "service_name",
      key: "service_name",
    },
    {
      title: "Actions",
      key: "actions",
      render: (text, record) => (
        <Actions
          isActive={true}
          onClickActive={() => handleActive(record._id, record.active)}
          recordActiveStatus={record.active}
          isEdit={true}
          isDelete={true}
          onClickDelete={() => {
            setDeleteId(record._id);
            setShowDelete(true);
          }}
          onClickEdit={() =>
            navigate(`/doctor-clinic-service-edit/${record._id}`)
          }
        />
      ),
    },
  ];

  const dataSource = records.map((el) => ({ ...el, key: el._id }));
  const breadcrumbsList = useMemo(
    () => [
      {
        text: "Doctor",
        link: "",
      },
      {
        text: "Clinic Services",
        link: "",
      },
    ],
    [],
  );

  useEffect(() => {
    records && setTimeout(() => setLoader(false));
  }, [records]);

  return (
    <ListPageLayout
      breadcrumbs={
        <Breadcrumbs
          className="healthsyMasterBreadCrumbs"
          breadcrumbsList={breadcrumbsList}
        />
      }
      addButton={<FilledButton props={addClinicService} />}
      filters={
        <DoctorsFilterOptions
          limit={pagination.limit}
          setLimit={(val) =>
            setPagination((prev) => ({
              ...prev,
              limit: val,
              skip: 0,
              currentPage: 1,
            }))
          }
          search={search}
          onChange={debouncedSearch}
          handleSortClick={handleSortClick}
          sortName={sortName}
          filter_records={() => {}}
          setFilterShow={() => {}}
          showFilter={false}
        />
      }
      tableProps={{
        columns,
        dataSource,
      }}
      totalCount={totalCount}
      pagination={pagination}
      onPageChange={(selected) => handlePageChange({ selected })}
      loader={loader}
    >
      {/* Optional children like modals */}
      {showDelete && (
        <Deletepopup
          handleDelete={() => handleDelete(deleteId)}
          id={deleteId}
          handleClose={() => setShowDelete(false)}
        />
      )}
    </ListPageLayout>
  );
};

export default ClinicService;
